import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Input, Radio, Row } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { EnrollmentFormContext } from '../../../../../../../providers/EnrollmentFormContextProvider';
import { rutValidator } from '../../../../../../../utils/validators';
import InputRut from '../../../../../../../components/form/inputs/InputRut';
import IdentificationType from '../../../../../../../enums/identificationType';
import ParamSelect from '../../../../../../params/components/ParamSelect';
import paramEndpoints from '../../../../../../params/paramEndpoints';
import FileSelect from '../../../../../../../components/form/upload/SingleUpload';
import AloneCol from '../../../../../../../components/common/AloneCol';
import InputGeo from '../../../../../../../components/common/inputs/InputGeo';
import EnrollmentView from '../../../../../../../enums/EnrollmentView';
import { EnrollmentContext } from '../../../../../../../providers/EnrollmentContextProvider';
import enrollmentAction from '../../../../../../../providers/EnrollmentContextProvider/enrollmentAction';
import form from '../../../../../../../utils/form';

const LeaderForm = ({ field, remove }) => {
  const { name } = field;

  const [selectedRut, setSelectedRut] = useState(false);
  const { enrollmentForm } = useContext(EnrollmentFormContext);
  const { dispatch } = useContext(EnrollmentContext);

  const toggleRutCheck = () => {
    setSelectedRut(true);
    enrollmentForm.setFieldsValue({
      declaringPerson: {
        identification: '',
      },
    });
  };

  const togglePassportCheck = () => {
    setSelectedRut(false);
    enrollmentForm.setFieldsValue({
      declaringPerson: {
        identification: '',
      },
    });
  };

  const handleSubmit = () => {
    dispatch({ type: enrollmentAction.GO_TO_VIEW, payload: EnrollmentView.LegalPersonForm });
  };

  const allowLettersAndWS = RegExp(
    '^(s*[a-zA-ZáéíóúAÁÉÍÓÚäëïöüÄËÏÖÜ\u00f1\u00d1]*)+(s*[a-zA-ZáéíóúAÁÉÍÓÚäëïöüÄËÏÖÜ\u00f1\u00d1]*)*[a-zA-ZáéíóúAÁÉÍÓÚäëïöüÄËÏÖÜ\u00f1\u00d1 ]*$'
  );
  return (
    <>
      <Form
        form={enrollmentForm}
        layout="vertical"
        onFinish={handleSubmit}
        validateMessages={form.validateMessages}
      >
        <Row>
          <Col span={23} className="formComplementaryInfo__form">
            <Form.Item
              label="Nombre completo"
              name={[name, 'name']}
              rules={[
                {
                  required: true,
                  message: 'Nombre es requerido',
                },
                {
                  pattern: allowLettersAndWS,
                  message: 'ingrese solo letras',
                },
              ]}
            >
              <Input maxLength={128} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={18}>
            <Form.Item name={[name, 'identificationType']} rules={[{ required: true }]}>
              <Radio.Group defaultValue={1}>
                <Radio value={IdentificationType.RUT} onChange={toggleRutCheck}>
                  Rut
                </Radio>
                <Radio value={IdentificationType.PASAPORTE} onChange={togglePassportCheck}>
                  Pasaporte
                </Radio>
                <Radio value={IdentificationType.OTRO} onChange={togglePassportCheck}>
                  Otro
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <AloneCol lg={11} xs={23}>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <Form.Item
                  label="N° de Identificación"
                  name={[name, 'identification']}
                  rules={[
                    {
                      required: true,
                      validator: selectedRut ? rutValidator : undefined,
                      message: 'Rut invalido',
                    },
                  ]}
                >
                  {selectedRut ? <InputRut /> : <Input type="text" maxLength={12} />}
                </Form.Item>
              )}
            </Form.Item>
          </AloneCol>
        </Row>
        <InputGeo
          form={enrollmentForm}
          countryName={[name, 'address', 'country']}
          regionName={[name, 'address', 'region']}
          cityName={[name, 'address', 'city']}
          required
        />
        <Row>
          <Col lg={11} xs={23}>
            <Form.Item label="Calle/Avenida" name={[name, 'address', 'streetName']}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col lg={5} xs={23}>
            <Form.Item
              label="Número"
              name={[name, 'address', 'streetNumber']}
              rules={[{ required: true }]}
            >
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 5, offset: 1 }} xs={23}>
            <Form.Item label="Depto/Casa" name={[name, 'address', 'houseNumber']}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col lg={11} xs={23}>
            <Form.Item label="Teléfono" name={[name, 'phone']} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 11, offset: 1 }} xs={23}>
            <Form.Item
              label="E-mail"
              name={[name, 'email']}
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: 'Email es requerido',
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col lg={11} xs={23}>
            <Form.Item
              label="Forma de actuación / Tipo de orden"
              name={[name, 'orderType']}
              rules={[{ required: true }]}
            >
              <ParamSelect paramType={paramEndpoints.ORDER_TYPES} />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col lg={19} xs={23}>
            <Form.Item
              label="¿La persona posee mandato legal para representar al titular?"
              name={[name, 'hasPowerToRepresentate']}
              rules={[{ required: true }]}
            >
              <Radio.Group
                options={[
                  { label: 'Si', value: true },
                  { label: 'No', value: false },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col lg={13} xs={23}>
            <Row>
              <Col>Escritura donde consta el poder o mandato (si corresponde)</Col>
            </Row>
            <Row>
              <Col className="formComplementaryInfo__fileInfo">
                Archivos JPG, PNG o PDF - Tamaño máximo 5MB.
              </Col>
            </Row>
          </Col>
          <Col className="formComplementaryInfo__info" lg={{ span: 8, offset: 1 }} xs={23}>
            <InfoCircleOutlined style={{ marginRight: '5px', color: '#B22938' }} />
            ¿Por qué pedimos esto?
          </Col>
        </Row>
        <Row gutter={[40]}>
          <Col span={12}>
            <Form.Item
              name={[name, 'power']}
              rules={[{ required: true, message: 'Documento obligatorio' }]}
            >
              <FileSelect accept={'image/*,.pdf'} maxSizeKb={10240}>
                Cargar documento
              </FileSelect>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={10}>
            <Button className="formComplementaryInfo__delete" onClick={() => remove(name)}>
              - Eliminar persona
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};

LeaderForm.propTypes = {
  field: PropTypes.shape().isRequired,
  remove: PropTypes.func.isRequired,
};

export default LeaderForm;
